import { useMemo } from "react";

import { NullableRecord, filterNullableRecord, filterTruthy, removeDuplicates } from "@bridgesplit/utils";

import { useLendingStrategyInfosQuery } from "../reducers";
import { BsMetaUtil, calculateFixedStrategyApy, calculateWAvgApy, useAbfTypesToUiConverter } from "../utils";
import { LendingStrategyExpanded, LendingStrategyFilter, StrategyExpanded } from "../types";
import { useOraclePrices } from "./pricing";
import { useMarketOracleInfos } from "./markets";
import { useActiveWallet } from "./wallet";
import { useGenerateUiExpanded } from "../utils/ui-expanded";

export function useStrategies(filter: LendingStrategyFilter, skip?: boolean) {
    const { data: rawData, isLoading: queryLoading, isFetching } = useLendingStrategyInfosQuery(filter, { skip });

    const strategies = Object.values(rawData ?? {});
    const principalMints = strategies.map((s) => s.strategy.principalMint);
    const collateralMints = strategies.flatMap((s) => Object.keys(s.terms));
    const mints = removeDuplicates(collateralMints.concat(principalMints));

    const { tokensLoading, getMetadata, convertStrategyInfo } = useAbfTypesToUiConverter(mints);
    const { createStrategyCollateralExpanded, isLoading: isUiLoading } = useGenerateUiExpanded(mints);

    const { getUsdPrice, isLoading: pricesLoading } = useOraclePrices(principalMints);

    const { data: marketData, isLoading: marketLoading } = useMarketOracleInfos({
        filter: {
            principalMints: [],
            addresses: strategies.map((s) => s.strategy.marketInformation)
        }
    });

    const isLoading = queryLoading || tokensLoading || pricesLoading || marketLoading || isUiLoading;

    const data = useMemo(() => {
        if (isLoading) return undefined;
        if (skip || !strategies) return [];
        return strategies
            .map((d): NullableRecord<StrategyExpanded> => {
                const strategyInfo = convertStrategyInfo(d);

                const fixedApy = calculateFixedStrategyApy(strategyInfo);
                const wAvgApy = calculateWAvgApy(strategyInfo, fixedApy);

                const lendingStrategyExpanded: LendingStrategyExpanded = {
                    ...strategyInfo.strategy,
                    fixedApy,
                    wAvgApy
                };

                // Find matching market information for this strategy

                const strategyMarketInfo = marketData?.find(
                    (info) => info.marketInformation.address === strategyInfo.strategy.marketInformation
                );

                const collateral = createStrategyCollateralExpanded(strategyInfo, strategyMarketInfo);

                const principalMetadata = getMetadata(d.strategy.principalMint);
                const principalUsdPrice = getUsdPrice(d.strategy.principalMint);

                return {
                    ...strategyInfo,
                    totalBalance: (strategyInfo.externalYieldInfo?.balance ?? 0) + strategyInfo.strategy.tokenBalance,
                    collateral,
                    principalUsdPrice,
                    principalMetadata,
                    strategy: lendingStrategyExpanded
                };
            })
            .filter(filterNullableRecord);
    }, [
        isLoading,
        skip,
        strategies,
        marketData,
        convertStrategyInfo,
        createStrategyCollateralExpanded,
        getMetadata,
        getUsdPrice
    ]);

    return {
        data: isLoading ? undefined : data,
        isLoading,
        isFetching
    };
}
export function useLenderStrategies(params?: { skip?: boolean }) {
    const { activeWallet } = useActiveWallet();

    const query = useStrategies({ userAddress: activeWallet }, !activeWallet || params?.skip);

    return query;
}

export function getStrategyName(strategyExpanded: StrategyExpanded | undefined) {
    if (!strategyExpanded) return "";
    const { principalMetadata } = strategyExpanded;

    const principalSymbol = BsMetaUtil.getName(principalMetadata);

    if (strategyExpanded.collateral.length === 1) {
        return `${principalSymbol}/${BsMetaUtil.getSymbol(strategyExpanded.collateral[0].metadata)}`;
    }

    const collateralTags = removeDuplicates(
        strategyExpanded.collateral
            .map((c) => c.metadata.tags)
            .flat()
            .filter(filterTruthy)
    );

    if (collateralTags.length === 1) {
        return `${principalSymbol}/${collateralTags[0]}`;
    }

    return principalSymbol;
}

export function getFirstStrategy(strategies: StrategyExpanded[] | undefined): StrategyExpanded | undefined {
    return strategies?.[0];
}
